import React, { useState } from 'react';
import { Shield, AlertTriangle, Eye, Clock, MapPin, TrendingUp, Search, Filter, RefreshCw, Download, Upload } from 'lucide-react';
import { StatsCard } from '../ui';
import Button from '../ui/Button';
import './SettingsPage.css';
import { apiService } from '../../services/api';

interface SuspiciousTransaction {
  id: string;
  transactionId: string;
  card: string;
  holder: string;
  amount: string;
  location: string;
  time: string;
  riskScore: string;
  reason: string;
}

interface FraudAlert {
  id: string;
  type: string;
  severity: 'Critical' | 'High' | 'Medium';
  description: string;
  cardNumber: string;
  time: string;
}

interface RiskLocation {
  location: string;
  riskLevel: 'High Risk' | 'Medium Risk' | 'Low Risk';
}

interface TimeRiskAnalysis {
  timeRange: string;
  fraudRate: string;
}

interface FraudData {
  fraudStats: {
    highRiskTransactions: string;
    highRiskTransactionsChange: string;
    highRiskTransactionsChangeType: 'increase' | 'decrease';
    blockedCards: string;
    blockedCardsChange: string;
    blockedCardsChangeType: 'increase' | 'decrease';
    falsePositives: string;
    falsePositivesChange: string;
    falsePositivesChangeType: 'increase' | 'decrease';
    responseTime: string;
    responseTimeChange: string;
    responseTimeChangeType: 'increase' | 'decrease';
  };
  suspiciousTransactions: SuspiciousTransaction[];
  fraudAlerts: FraudAlert[];
  riskLocations: RiskLocation[];
  timeRiskAnalysis: TimeRiskAnalysis[];
}

const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('live-alerts');
  const [searchTerm, setSearchTerm] = useState('');
  const [fraudData, setFraudData] = useState<FraudData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filterRisk, setFilterRisk] = useState('All');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const fetchFraudData = async () => {
    try {
      setLoading(true);
      const data = await apiService.getFraudAnalytics();
      setFraudData(data);
    } catch (error) {
      console.error('Failed to load fraud data:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchFraudData();
  }, []);

  const filteredTransactions = (fraudData?.suspiciousTransactions || []).filter(transaction => {
    const matchesSearch = (transaction.transactionId || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (transaction.holder || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (transaction.location || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesRisk = true;
    const score = parseInt(transaction.riskScore);
    if (filterRisk === 'High') matchesRisk = score > 80;
    else if (filterRisk === 'Medium') matchesRisk = score > 60 && score <= 80;
    else if (filterRisk === 'Low') matchesRisk = score <= 60;
    
    return matchesSearch && matchesRisk;
  });

  const handleExport = () => {
    // Generate CSV from current active tab data or suspicious transactions
    const dataToExport = activeTab === 'suspicious-transactions' 
      ? filteredTransactions 
      : (fraudData?.fraudAlerts || []);
      
    if (dataToExport.length === 0) {
      alert('No data available to export.');
      return;
    }
    
    const headers = Object.keys(dataToExport[0]).join(',');
    const rows = dataToExport.map(obj => Object.values(obj).map(val => `"${val}"`).join(','));
    const csvContent = [headers, ...rows].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Simulate an import process
      setLoading(true);
      setTimeout(() => {
        alert(`Successfully imported ${file.name}`);
        setLoading(false);
        // Reset file input
        if (fileInputRef.current) fileInputRef.current.value = '';
      }, 1000);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'live-alerts':
        return (
          <div className="live-alerts-content">
            <div className="fraud-alerts-section">
              <div className="section-header">
                <h3>Active Fraud Alerts</h3>
                <span className="mark-all-reviewed">Mark All as Reviewed</span>
              </div>
              <div className="alerts-list">
                {fraudData?.fraudAlerts?.length === 0 && <p className="p-4 text-sm text-gray-500">No active alerts at this time.</p>}
                {(fraudData?.fraudAlerts || []).map((alert) => (
                  <div key={alert.id} className={`alert-card ${alert.severity.toLowerCase()}`}>
                    <div className="alert-icon">
                      <AlertTriangle size={20} />
                    </div>
                    <div className="alert-content">
                      <div className="alert-header">
                        <span className="alert-type">{alert.type}</span>
                        <span className={`alert-severity ${alert.severity.toLowerCase()}`}>
                          {alert.severity}
                        </span>
                      </div>
                      <p className="alert-description">{alert.description}</p>
                      <div className="alert-details">
                        <span>{alert.cardNumber}</span>
                        <span>{alert.time}</span>
                      </div>
                    </div>
                    <div className="alert-actions">
                      <Button variant="secondary" size="sm">
                        <Eye size={14} />
                        Investigate
                      </Button>
                      <Button variant="primary" size="sm">
                        Block Card
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'suspicious-transactions':
        return (
          <div className="suspicious-transactions-content">
            <div className="section-header">
              <h3>Suspicious Transactions Requiring Review</h3>
            </div>
            <div className="transactions-table">
              <table>
                <thead>
                  <tr>
                    <th>Transaction ID</th>
                    <th>Card</th>
                    <th>Holder</th>
                    <th>Amount</th>
                    <th>Location</th>
                    <th>Time</th>
                    <th>Risk Score</th>
                    <th>Reason</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((transaction) => (
                    <tr key={transaction.id}>
                      <td>{transaction.transactionId}</td>
                      <td>{transaction.card}</td>
                      <td>{transaction.holder}</td>
                      <td>{transaction.amount}</td>
                      <td>{transaction.location}</td>
                      <td>{transaction.time}</td>
                      <td>
                        <span className={`risk-score ${parseInt(transaction.riskScore) > 80 ? 'high' : parseInt(transaction.riskScore) > 60 ? 'medium' : 'low'}`}>
                          {transaction.riskScore}
                        </span>
                      </td>
                      <td>{transaction.reason}</td>
                      <td>
                        <div className="transaction-actions">
                          <Button variant="secondary" size="sm">
                            <Eye size={14} />
                          </Button>
                          <Button variant="secondary" size="sm">
                            <Eye size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'risk-patterns':
        return (
          <div className="risk-patterns-content">
            <div className="risk-sections">
              <div className="risk-by-location">
                <div className="section-header">
                  <h3>Risk Patterns by Location</h3>
                </div>
                <div className="risk-locations-list">
                  {(fraudData?.riskLocations || []).map((location, index) => (
                    <div key={index} className="risk-location-item">
                      <div className="location-info">
                        <MapPin size={16} />
                        <span className="location-name">{location.location}</span>
                      </div>
                      <span className={`risk-level ${location.riskLevel.toLowerCase().replace(' ', '-')}`}>
                        {location.riskLevel}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="time-based-analysis">
                <div className="section-header">
                  <h3>Time-based Risk Analysis</h3>
                </div>
                <div className="time-risk-list">
                  {(fraudData?.timeRiskAnalysis || []).map((timeRisk, index) => (
                    <div key={index} className="time-risk-item">
                      <div className="time-info">
                        <Clock size={16} />
                        <span className="time-range">{timeRisk.timeRange}</span>
                      </div>
                      <span className="fraud-rate">{timeRisk.fraudRate}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div className="settings-title">
          <Shield size={24} />
          <h1>Fraud Detection & Security</h1>
        </div>
        <div className="header-actions">
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileChange} 
            style={{ display: 'none' }} 
            accept=".csv,.json" 
          />
          <Button variant="secondary" className="action-btn" onClick={fetchFraudData}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </Button>
          <Button variant="secondary" className="action-btn" onClick={handleExport}>
            <Download size={16} />
            Export
          </Button>
          <Button variant="primary" className="action-btn" onClick={handleImportClick}>
            <Upload size={16} />
            Import
          </Button>
        </div>
      </div>

      <div className="fraud-stats">
        <StatsCard
          title="High Risk Transactions"
          value={loading || !fraudData ? '...' : fraudData.fraudStats.highRiskTransactions}
          trend={{
            label: !fraudData ? '...' : fraudData.fraudStats.highRiskTransactionsChange,
            type: fraudData?.fraudStats.highRiskTransactionsChangeType || 'increase'
          }}
          icon={<AlertTriangle size={24} />}
          color="orange"
        />
        <StatsCard
          title="Blocked Cards"
          value={loading || !fraudData ? '...' : fraudData.fraudStats.blockedCards}
          trend={{
            label: !fraudData ? '...' : fraudData.fraudStats.blockedCardsChange,
            type: fraudData?.fraudStats.blockedCardsChangeType || 'increase'
          }}
          icon={<Shield size={24} />}
          color="orange"
        />
        <StatsCard
          title="False Positives"
          value={loading || !fraudData ? '...' : fraudData.fraudStats.falsePositives}
          trend={{
            label: !fraudData ? '...' : fraudData.fraudStats.falsePositivesChange,
            type: fraudData?.fraudStats.falsePositivesChangeType || 'decrease'
          }}
          icon={<TrendingUp size={24} />}
          color="green"
        />
        <StatsCard
          title="Response Time"
          value={loading || !fraudData ? '...' : fraudData.fraudStats.responseTime}
          trend={{
            label: !fraudData ? '...' : fraudData.fraudStats.responseTimeChange,
            type: fraudData?.fraudStats.responseTimeChangeType || 'decrease'
          }}
          icon={<Clock size={24} />}
          color="blue"
        />
      </div>

      <div className="fraud-content">
        <div className="fraud-tabs">
          <button
            className={`tab-button ${activeTab === 'live-alerts' ? 'active' : ''}`}
            onClick={() => setActiveTab('live-alerts')}
          >
            Live Alerts
          </button>
          <button
            className={`tab-button ${activeTab === 'suspicious-transactions' ? 'active' : ''}`}
            onClick={() => setActiveTab('suspicious-transactions')}
          >
            Suspicious Transactions
          </button>
          <button
            className={`tab-button ${activeTab === 'risk-patterns' ? 'active' : ''}`}
            onClick={() => setActiveTab('risk-patterns')}
          >
            Risk Patterns
          </button>
        </div>

        {activeTab === 'suspicious-transactions' && (
          <div className="table-controls-wrapper">
            <div className="table-controls">
              <div className="search-container">
                <Search size={16} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search transactions, cards, companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
              <Button 
                variant="secondary" 
                className={`filter-btn ${showFilters ? 'active' : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter size={16} />
                <span>Filter</span>
              </Button>
            </div>
            
            {showFilters && (
              <div className="advanced-filters" style={{ display: 'flex', gap: '16px', marginTop: '16px', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
                <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 500, color: '#6c757d' }}>Risk Level</label>
                  <select 
                    value={filterRisk} 
                    onChange={(e) => setFilterRisk(e.target.value)}
                    style={{ padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: '6px', fontSize: '14px', outline: 'none' }}
                  >
                    <option value="All">All Risks</option>
                    <option value="High">High Risk</option>
                    <option value="Medium">Medium Risk</option>
                    <option value="Low">Low Risk</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="tab-content">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
